// Dev mock — used when the game runs OUTSIDE the Gamma Games app (a plain
// browser, local dev server, or another portal) so developers can build and
// test against the same API without the real host bridge. State lives in
// memory and is mirrored to localStorage so it survives a page reload.

import type {
  BridgeResult,
  ChallengeContext,
  CoinValue,
  GammaConfig,
  HostSdk,
  Player,
  ProgressData,
} from './types';

const MOCK_BALANCE_KEY = '__gamma_mock_balance';
const MOCK_PROGRESS_KEY = '__gamma_mock_progress';
const REASON_RE = /^[a-z0-9_]+$/;

const DEFAULT_PLAYER: Player = {
  id: 'mock-player',
  displayName: 'Dev Player',
  avatar: 'avatar_ninja',
  frame: null,
  isSubscribed: false,
};

const DEFAULT_COIN_VALUE: CoinValue = {
  usdPerCoin: 0.01,
  currencyCode: 'USD',
  displayName: 'Gamma Coins',
  symbol: 'GC',
  iconUrl: null,
};

function readNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* ignore quota / disabled storage */
  }
}

/**
 * Builds a {@link HostSdk}-shaped object backed by an in-memory + localStorage
 * store. Mirrors the real bridge's validation (amount/reason caps) so a game
 * that works against the mock works against the host.
 */
export function createMockHost(config: GammaConfig): HostSdk {
  // `config.mock` may be `true` (use defaults) or an object of seed values.
  const seed = typeof config.mock === 'object' && config.mock ? config.mock : {};
  const player: Player = { ...DEFAULT_PLAYER, ...(seed.player ?? {}) };
  const coinValue: CoinValue = {
    ...DEFAULT_COIN_VALUE,
    ...(seed.coinValue ?? {}),
  };
  const seedBalance = seed.coins ?? 500;

  // Optional seeded challenge (memory only; not persisted across reloads).
  const challenge: ChallengeContext | null = seed.challenge
    ? {
        challengeId: 1,
        type: 'high_score',
        mode: 'async',
        param: {},
        wagerCoins: 100,
        deadline: null,
        myRole: 'challenger',
        opponent: { displayName: 'Mock Rival', avatar: null },
        ...seed.challenge,
      }
    : null;
  let challengeCompleted = false;

  // Persisted so reloads keep the same dev balance.
  let balance = readNumber(MOCK_BALANCE_KEY, seedBalance);
  writeNumber(MOCK_BALANCE_KEY, balance);

  const ok = (extra: Record<string, unknown>): BridgeResult => ({
    success: true,
    ...extra,
  });
  const err = (code: BridgeResult['error']): BridgeResult => ({
    success: false,
    error: code,
  });

  return {
    ready: true,
    version: 'mock',

    getCoins(cb) {
      cb(ok({ balance }));
    },

    spendCoins(amount, reason, cb) {
      if (!Number.isInteger(amount) || amount <= 0 || amount > 10000) {
        return cb(err('invalid_amount'));
      }
      if (typeof reason !== 'string' || !REASON_RE.test(reason) || reason.length > 64) {
        return cb(err('invalid_reason'));
      }
      if (balance < amount) {
        return cb({ success: false, error: 'insufficient_coins', balance });
      }
      balance -= amount;
      writeNumber(MOCK_BALANCE_KEY, balance);
      cb(ok({ newBalance: balance, spent: amount }));
    },

    earnCoins(amount, reason, cb) {
      if (!Number.isInteger(amount) || amount <= 0 || amount > 100) {
        return cb(err('invalid_amount'));
      }
      if (typeof reason !== 'string' || !REASON_RE.test(reason) || reason.length > 64) {
        return cb(err('invalid_reason'));
      }
      balance += amount;
      writeNumber(MOCK_BALANCE_KEY, balance);
      cb(ok({ newBalance: balance, earned: amount }));
    },

    saveProgress(data: ProgressData, cb) {
      let encoded: string;
      try {
        encoded = JSON.stringify(data);
      } catch {
        return cb(err('serialization_error'));
      }
      if (encoded.length > 100 * 1024) return cb(err('data_too_large'));
      try {
        localStorage.setItem(MOCK_PROGRESS_KEY, encoded);
      } catch {
        return cb(err('internal_error'));
      }
      cb(ok({ sizeBytes: encoded.length }));
    },

    loadProgress(cb) {
      try {
        const raw = localStorage.getItem(MOCK_PROGRESS_KEY);
        cb(ok({ data: raw ? (JSON.parse(raw) as ProgressData) : null }));
      } catch {
        cb(ok({ data: null }));
      }
    },

    getPlayer(cb) {
      cb(ok({ player }));
    },

    getCoinValue(cb) {
      cb(ok({ coinValue }));
    },

    getChallenge(cb) {
      cb(ok({ challenge }));
    },

    submitChallengeScore(value, cb) {
      if (!challenge) return cb(err('unknown_type'));
      if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return cb(err('invalid_amount'));
      }
      cb(ok({}));
    },

    completeChallenge(result, cb) {
      if (!challenge) return cb(err('unknown_type'));
      if (challengeCompleted) return cb(err('internal_error'));
      const key =
        challenge.type === 'fastest_time'
          ? 'timeMs'
          : challenge.type === 'level_streak'
            ? 'streak'
            : 'score';
      const value = (result as Record<string, unknown>)?.[key];
      if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return cb(err('invalid_amount'));
      }
      challengeCompleted = true;
      // Dev mock: pretend the opponent already finished and we won.
      cb(ok({ settled: true, outcome: 'won', potCoins: challenge.wagerCoins * 2 }));
    },
  };
}
